import {
  Autocomplete as MuiAutocomplete,
  autocompleteClasses,
} from '@mui/material'
import { IconArrow } from 'assets/icons'
import { MenuOption } from 'elements/navigation'
import { useBoolean } from 'lib/hooks'
import { checkScrollToBottom } from 'lib/js-utils'
import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormControl, FormControlWrapper } from '../form'
import { InputBase } from '../input-base'
import { AutocompletePopper } from './autocomplete-popper'
import { LoadingMoreOption } from './loading-more-option'

type Option = {
  value: string
  label: string
  disabled?: boolean
  content?: React.ReactNode
}

export type AutocompleteProps = FormControlWrapper & {
  value: string
  onChange: (value: string, option: Option | null) => void
  options: Array<Option>

  onOpen?: () => void
  onClose?: () => void
  onInputChange?: (value: string) => void
  disabled?: boolean
  loading?: boolean
  loadingError?: boolean
  placeholder?: string
  noOptionsText?: string
  disableInternalFilter?: boolean
  closedWhileInputEmpty?: boolean
  disableAlphabeticalOrder?: boolean
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  hasMoreOptions?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
}

export const Autocomplete = forwardRef(
  (props: AutocompleteProps, ref: React.Ref<HTMLInputElement>) => {
    const { t } = useTranslation()
    const isOpen = useBoolean(false)
    const [inputValue, setInputValue] = useState('')

    const mainOptions = [...props.options].sort((a, b) =>
      props.disableAlphabeticalOrder ? 0 : a.label.localeCompare(b.label),
    )

    const options = props.isLoadingMore
      ? [
          ...mainOptions,
          {
            value: 'LOADING_MORE',
            label: t('common.loading_more'),
            disabled: true,
            content: <LoadingMoreOption />,
          },
        ]
      : mainOptions

    const findOptionByValue = (value: string) =>
      options.find(option => option.value === value) ?? null

    return (
      <FormControl
        label={props.label}
        id={props.id}
        error={props.error}
        helperText={props.helperText}
        onRemove={props.onRemove}
        required={props.required}
      >
        <MuiAutocomplete
          ref={ref}
          value={props.value || null}
          onChange={(_event, newValue) => {
            if (newValue) {
              props.onChange(newValue, findOptionByValue(newValue))
            } else {
              props.onChange('', null)
            }
          }}
          options={options.map(option => option.value)}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue)
            if (props.onInputChange) {
              props.onInputChange(newInputValue)
            }
          }}
          open={
            isOpen.isTrue &&
            (props.closedWhileInputEmpty ? inputValue !== '' : true)
          }
          onOpen={() => {
            isOpen.setTrue()
            props?.onOpen?.()
          }}
          onClose={() => {
            isOpen.setFalse()
            props?.onClose?.()
          }}
          popupIcon={<IconArrow />}
          loading={props.loading}
          disabled={props.disabled}
          getOptionLabel={optionValue =>
            findOptionByValue(optionValue)?.label ?? ''
          }
          getOptionDisabled={optionValue =>
            Boolean(findOptionByValue(optionValue)?.disabled ?? false)
          }
          noOptionsText={
            props.loadingError
              ? t('common.failed_to_load_options')
              : props.noOptionsText
          }
          filterOptions={
            props.disableInternalFilter ? options => options : undefined
          }
          renderInput={params => (
            <InputBase
              {...params.InputProps}
              endAdornment={
                props.endAdornment ?? params.InputProps.endAdornment
              }
              startAdornment={
                props.startAdornment ?? params.InputProps.startAdornment
              }
              inputProps={{
                ...params.inputProps,
                id: props.id ?? props.label,
                type: 'search',
                autoComplete: 'off',
              }}
              error={props.error}
              disabled={props.disabled}
              placeholder={props.placeholder}
              required={props.required}
            />
          )}
          renderOption={({ className, ...optionProps }, optionValue) => {
            const option = findOptionByValue(optionValue)
            return option ? (
              <MenuOption
                {...optionProps}
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                disableRipple
              >
                {option.content ?? option.label}
              </MenuOption>
            ) : null
          }}
          PopperComponent={AutocompletePopper}
          ListboxProps={{
            onScroll: event => {
              if (
                props.onLoadMore &&
                props.hasMoreOptions &&
                !props.isLoadingMore
              ) {
                checkScrollToBottom(event, props.onLoadMore)
              }
            },
          }}
          sx={{
            width: '100%',

            [`& .${autocompleteClasses.inputRoot}`]: {
              width: '100%',
              height: 'auto',
              minHeight: 48,
              py: 0.5,
              px: 1,

              ...(isOpen.isTrue && {
                borderBottomRightRadius: 0,
                borderBottomLeftRadius: 0,
              }),

              [`& .${autocompleteClasses.input}`]: {
                py: 0.5,
                pl: 2,
                pr: 1,
                minWidth: 100,
              },
            },

            [`& .${autocompleteClasses.endAdornment}`]: {
              right: '8px',
            },
          }}
        />
      </FormControl>
    )
  },
)
